// ─────────────────────────────────────────────────────────────────────────────
// V2Invoices — /dashboard/viewinvoices in the v2 design (the LIST only).
// Opening an invoice shows v1's own invoice view, unchanged (document +
// action bar: print, PDF, IRN, cancel, delete) — the invoice itself will be
// redesigned separately.
// Endpoints are v1's: fetchallinvoices, search, bulk/mark-paid, bulk/download-ids,
// cancel/:id, payment/:id, /api/pdf/generate/:id, /api/irn/generate/:id,
// /api/exports/invoices. WhatsApp actions reuse utils/whatsapp.js.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import V2InvoiceView from "./V2InvoiceView";
import { sendInvoiceViaWhatsApp, sendPaymentReminderViaWhatsApp } from "../utils/whatsapp";
import { Glyph, Duo } from "./icons";
import { MenuButton, Segmented, Sheet, Skel, inr, useMedia } from "./primitives";
import { RecordPaymentSheet } from "./V2Home";

const BASE = `${API_BASE}/api`;
const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const clientOf = (inv) => inv.clientName || inv.clientId?.clientName || "Unknown client";
const phoneOf = (inv) => inv.clientPhone || inv.clientId?.clientPhone || "";
const dueOf = (inv) => (inv.grandTotal || 0) - (inv.amountPaid || 0);
const overdue = (inv) => {
  if (inv.paymentStatus === "PAID" || inv.status !== "ACTIVE") return false;
  const days = (Date.now() - new Date(inv.invoiceDate).getTime()) / 86400000;
  return days > (inv.paymentTimeline || 30);
};
const selectable = (inv) => inv.status === "ACTIVE" && inv.paymentStatus !== "PAID";

function PayStatus({ inv }) {
  if (inv.status === "CANCELLED") return <span className="v2-status v2-status--bad"><Glyph name="close" size={12} stroke={2.4} /> Cancelled</span>;
  if (inv.paymentStatus === "PAID") return <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> Paid</span>;
  if (overdue(inv)) return <span className="v2-status v2-status--bad"><Glyph name="alert" size={13} stroke={2.2} /> Overdue</span>;
  if (inv.paymentStatus === "PARTIAL") return <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> {inr(dueOf(inv))} due</span>;
  return <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> Unpaid</span>;
}

function Check({ checked, onChange, label, indeterminate }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <label className="v2i-check" onClick={(e) => e.stopPropagation()}>
      <input ref={ref} type="checkbox" checked={checked} onChange={onChange} aria-label={label} />
      <span aria-hidden="true"><Glyph name="check" size={12} stroke={3} /></span>
    </label>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Invoices({ showAlert }) {
  usePageTitle("Invoices");
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("invoices", "write");
  const canExport = hasPermission("reports", "read");
  const isPhone = useMedia("(max-width: 760px)");

  const [tab, setTab] = useState("ACTIVE");
  const [pay, setPay] = useState("ALL");
  const [view, setView] = useState(() => { try { return localStorage.getItem("eb-v2-inv-view") || "list"; } catch { return "list"; } });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ invoices: [], pagination: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);   // null = not searching
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [openInv, setOpenInv] = useState(null);   // invoice opened in V2InvoiceView
  const [invVersion, setInvVersion] = useState(0); // bump to refresh the open invoice after an action
  const backOut = useRef(false);                   // opened via /dashboard/invoice/:id → Back returns to the previous page
  useEffect(() => {
    const id = location.state?.openInvoiceId;
    if (!id) return;
    setOpenInv({ _id: id });
    backOut.current = !!location.state?.back && window.history.length > 1;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line
  const [open, setOpen] = useState({});           // grouped view: expanded clients

  // Sheets
  const [payFor, setPayFor] = useState(null);
  const [confirm, setConfirm] = useState(null);   // { kind: "cancel"|"irn"|"markpaid", inv? }
  const [exportOpen, setExportOpen] = useState(false);
  const payOrigin = useRef(null);
  const [busy, setBusy] = useState("");           // key of the running action
  const payInvoice = useMemo(() => (payFor ? { ...payFor, clientName: clientOf(payFor), amountPaid: payFor.amountPaid || 0 } : null), [payFor]);

  useEffect(() => { try { localStorage.setItem("eb-v2-inv-view", view); } catch { /* ignore */ } }, [view]);

  const load = useCallback(async (p = page) => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ page: p, limit: 50, status: tab });
      if (pay !== "ALL") params.append("paymentStatus", pay);
      const r = await fetch(`${BASE}/invoices/fetchallinvoices?${params}`, { headers: auth() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setData(Array.isArray(d) ? { invoices: d, pagination: null } : { invoices: d.invoices || [], pagination: d.pagination || null });
      setPage(p);
    } catch { setErr("Couldn't load invoices."); }
    finally { setLoading(false); }
  }, [tab, pay, page]);

  useEffect(() => { setSelected(new Set()); load(1); }, [tab, pay]); // eslint-disable-line

  // Debounced server search (same endpoint + 400ms as v1)
  useEffect(() => {
    const s = q.trim();
    if (!s) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BASE}/invoices/search?q=${encodeURIComponent(s)}`, { headers: auth() });
        const d = await r.json();
        setResults(Array.isArray(d) ? d : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const rows = useMemo(() => {
    if (results) {
      return results.filter((i) => i.status === tab && (pay === "ALL" || i.paymentStatus === pay));
    }
    return data.invoices;
  }, [results, data, tab, pay]);

  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((inv) => {
      const k = clientOf(inv);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(inv);
    });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const pageTotals = useMemo(() => ({
    billed: rows.reduce((s, i) => s + (i.grandTotal || 0), 0),
    due: rows.filter((i) => i.status === "ACTIVE").reduce((s, i) => s + Math.max(dueOf(i), 0), 0),
    overdue: rows.filter(overdue).length,
  }), [rows]);

  // ── Selection ──────────────────────────────────────────────────────────────
  const selectableIds = rows.filter(selectable).map((i) => i._id);
  const allSel = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSel = selectableIds.some((id) => selected.has(id));
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids) => setSelected((p) => {
    const n = new Set(p);
    const every = ids.every((id) => n.has(id));
    ids.forEach((id) => (every ? n.delete(id) : n.add(id)));
    return n;
  });

  // ── Actions ────────────────────────────────────────────────────────────────
  const say = (m, t = "success") => showAlert?.(m, t);

  // PDF design used by every download on this page (saved per browser)
  const [pdfTpl, setPdfTplState] = useState(() => { try { const v = localStorage.getItem("eb-pdf-template"); return ["v2", "tally", "classic"].includes(v) ? v : "v2"; } catch { return "v2"; } });
  const setPdfTpl = (v) => { setPdfTplState(v); try { localStorage.setItem("eb-pdf-template", v); } catch {} };

  const downloadPdf = async (inv) => {
    setBusy(`pdf:${inv._id}`);
    try {
      const r = await fetch(`${BASE}/pdf/generate/${inv._id}?template=${pdfTpl}`, { method: "POST", headers: auth() });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "PDF failed"); }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a"); a.href = url; a.download = `Invoice-${inv.invoiceNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { say(e.message || "Couldn't generate the PDF", "danger"); }
    finally { setBusy(""); }
  };

  const bulkCsv = async () => {
    setBusy("bulkcsv");
    try {
      const r = await fetch(`${BASE}/invoices/bulk/download-ids`, { method: "POST", headers: json(), body: JSON.stringify({ ids: [...selected] }) });
      if (!r.ok) throw new Error();
      const list = await r.json();
      const header = ["Invoice #", "Date", "Client", "Total", "Paid", "Balance", "Status"].join(",");
      const lines = list.map((inv) => [
        inv.invoiceNumber, new Date(inv.invoiceDate).toLocaleDateString("en-IN"),
        `"${inv.clientName || inv.clientId?.clientName || ""}"`, inv.grandTotal?.toFixed(2), inv.amountPaid?.toFixed(2),
        (inv.grandTotal - inv.amountPaid).toFixed(2), inv.paymentStatus,
      ].join(","));
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([[header, ...lines].join("\n")], { type: "text/csv" }));
      a.download = `Selected_Invoices_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
    } catch { say("Couldn't prepare the CSV", "danger"); }
    finally { setBusy(""); }
  };

  const runConfirm = async () => {
    const c = confirm;
    setBusy("confirm");
    try {
      if (c.kind === "cancel") {
        const r = await fetch(`${BASE}/invoices/cancel/${c.inv._id}`, { method: "PUT", headers: json() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || d.error || "Couldn't cancel the invoice");
        say(`${c.inv.invoiceNumber} cancelled — stock restored`);
      } else if (c.kind === "irn") {
        const r = await fetch(`${BASE}/irn/generate/${c.inv._id}`, { method: "POST", headers: auth() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || "IRN generation failed");
        say(`IRN generated · Ack ${d.ackNo || ""}`);
      } else if (c.kind === "markpaid") {
        const r = await fetch(`${BASE}/invoices/bulk/mark-paid`, { method: "POST", headers: json(), body: JSON.stringify({ ids: [...selected] }) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || "Couldn't mark as paid");
        say(`${selected.size} invoice${selected.size === 1 ? "" : "s"} marked paid`);
        setSelected(new Set());
      }
      setConfirm(null);
      setInvVersion((v) => v + 1);
      await load(page);
    } catch (e) {
      setConfirm((x) => (x ? { ...x, error: e.message } : x));
    } finally { setBusy(""); }
  };

  const menuFor = (inv) => {
    const phone = phoneOf(inv);
    const items = [
      { label: "Open invoice", glyph: "invoice", onSelect: () => setOpenInv(inv) },
      { label: busy === `pdf:${inv._id}` ? "Preparing PDF…" : "Download PDF", glyph: "download", onSelect: () => downloadPdf(inv) },
    ];
    if (canWrite && selectable(inv)) items.push({ label: "Record payment", glyph: "cash", onSelect: () => setPayFor(inv) });
    if (phone) {
      items.push("divider");
      items.push({ label: "Send on WhatsApp", glyph: "phone", onSelect: () => sendInvoiceViaWhatsApp(inv, phone) });
      if (inv.paymentStatus !== "PAID") items.push({ label: "Payment reminder", glyph: "bell", onSelect: () => sendPaymentReminderViaWhatsApp(inv, phone, clientOf(inv)) });
    }
    if (canWrite && inv.status === "ACTIVE") {
      items.push("divider");
      if (inv.irnStatus !== "generated") items.push({ label: "Generate IRN", glyph: "gst", onSelect: () => setConfirm({ kind: "irn", inv }) });
      items.push({ label: "Cancel invoice", glyph: "close", danger: true, onSelect: () => setConfirm({ kind: "cancel", inv }) });
    }
    return items;
  };

  // ── Invoice opened → v1's invoice view, unchanged ─────────────────────────
  const sheets = (
    <>
      <RecordPaymentSheet
        invoice={payInvoice}
        originRef={payOrigin} onClose={() => setPayFor(null)}
        onDone={(msg) => { setPayFor(null); setInvVersion((v) => v + 1); load(page); say(msg); }} />
      <ConfirmSheet state={confirm} busy={busy === "confirm"} onClose={() => setConfirm(null)} onConfirm={runConfirm} count={selected.size} />
    </>
  );

  // ── Invoice opened → v2 on-screen invoice (the downloadable PDF is unchanged) ──
  if (openInv) {
    return (
      <>
        <V2InvoiceView invoiceId={openInv._id} version={invVersion} canWrite={canWrite}
          onBack={() => { if (backOut.current) { backOut.current = false; navigate(-1); return; } setOpenInv(null); load(page); }}
          onPay={(inv) => setPayFor(inv)} onConfirm={setConfirm}
          onDownload={downloadPdf} downloading={busy === `pdf:${openInv._id}`} say={say} />
        {sheets}
      </>
    );
  }

  const pg = data.pagination;
  const total = pg?.total ?? rows.length;

  const renderRow = (inv, showClient = true) => {
    const sel = selected.has(inv._id);
    return (
      <div key={inv._id} className={`v2i-row${showClient ? "" : " v2i-row--nc"}${sel ? " is-sel" : ""}${inv.status === "CANCELLED" ? " is-void" : ""}`}>
        <span className="v2i-cell-check">
          {canWrite && selectable(inv) ? <Check checked={sel} onChange={() => toggle(inv._id)} label={`Select ${inv.invoiceNumber}`} /> : <span className="v2i-check-gap" />}
        </span>
        <button type="button" className="v2i-open" onClick={() => setOpenInv(inv)}>
          <span className="v2i-main">
            <span className="v2i-no">
              {inv.invoiceNumber}
              {inv.irnStatus === "generated" && <span className="v2i-irn" title="e-Invoice generated">IRN</span>}
            </span>
            <span className="v2i-sub">{showClient ? <>{clientOf(inv)} · </> : null}{fmtDate(inv.invoiceDate)}</span>
          </span>
          {!isPhone && showClient && <span className="v2i-client">{clientOf(inv)}</span>}
          {!isPhone && <span className="v2i-date">{fmtDate(inv.invoiceDate)}</span>}
          <span className="v2i-amount">
            <span className="v2i-total">{inr(inv.grandTotal, { decimals: 2 })}</span>
            {isPhone && <PayStatus inv={inv} />}
          </span>
          {!isPhone && <span className="v2i-status"><PayStatus inv={inv} /></span>}
        </button>
        <span className="v2i-actions">
          {!isPhone && phoneOf(inv) && inv.paymentStatus !== "PAID" && inv.status === "ACTIVE" && (
            <button type="button" className="v2-icon-btn v2i-quick" title="WhatsApp payment reminder"
              onClick={() => sendPaymentReminderViaWhatsApp(inv, phoneOf(inv), clientOf(inv))}>
              <Glyph name="bell" size={17} />
            </button>
          )}
          <MenuButton items={menuFor(inv)} className="v2-icon-btn v2i-more" label={`Actions for ${inv.invoiceNumber}`} width={236} />
        </span>
      </div>
    );
  };

  return (
    <div className="v2p v2i">
      <header className="v2p-head">
        <div>
          <h1 className="v2p-title">Invoices</h1>
          <p className="v2p-sub">
            {loading && !rows.length ? "Loading…" : results
              ? `${rows.length} match${rows.length === 1 ? "" : "es"} for “${q.trim()}”`
              : `${total} ${tab === "ACTIVE" ? "active" : "cancelled"} invoice${total === 1 ? "" : "s"}${pay !== "ALL" ? ` · ${pay.toLowerCase()}` : ""}`}
          </p>
        </div>
        <div className="v2i-head-actions">
          <div className="v2i-pdf-tpl" title="Design used when you download an invoice PDF">
            <span>PDF</span>
            <Segmented size="sm" ariaLabel="Invoice PDF design" value={pdfTpl} onChange={setPdfTpl}
              options={[{ value: "v2", label: "Modern" }, { value: "tally", label: "Classic" }, { value: "classic", label: "Original" }]} />
          </div>
          {canExport && (
            <button type="button" className="v2-btn" onClick={() => setExportOpen(true)}>
              <Glyph name="download" size={17} /> Export
            </button>
          )}
          {canWrite && (
            <button type="button" className="v2-btn v2-btn--primary" onClick={() => navigate("/dashboard/createinvoice")}>
              <Glyph name="plus" size={17} stroke={2.2} /> New invoice
            </button>
          )}
        </div>
      </header>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Invoice status" value={tab} onChange={setTab}
          options={[{ value: "ACTIVE", label: "Active" }, { value: "CANCELLED", label: "Cancelled" }]} />
        <Segmented size="sm" ariaLabel="Payment status" value={pay} onChange={setPay}
          options={[{ value: "ALL", label: "All" }, { value: "UNPAID", label: "Unpaid" }, { value: "PARTIAL", label: "Partial" }, { value: "PAID", label: "Paid" }]} />
        <div className="v2-field--search v2i-search">
          <Glyph name="search" size={17} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice no., client, 2026-10, 15-10-2026…" aria-label="Search invoices" />
          {searching ? <span className="v2-spin" /> : q && (
            <button type="button" className="v2c-clear" aria-label="Clear search" onClick={() => setQ("")}><Glyph name="close" size={13} stroke={2.2} /></button>
          )}
        </div>
        <Segmented size="sm" ariaLabel="Layout" value={view} onChange={setView}
          options={[{ value: "list", label: "List" }, { value: "client", label: "By client" }]} />
      </div>

      {/* ── Summary of what's on screen ─────────────────────────────────── */}
      {!!rows.length && tab === "ACTIVE" && (
        <div className="v2i-summary" aria-label="Shown invoices summary">
          <span><b>{inr(pageTotals.billed)}</b> billed</span>
          <span><b>{inr(pageTotals.due)}</b> outstanding</span>
          {pageTotals.overdue > 0 && <span className="is-bad"><b>{pageTotals.overdue}</b> overdue</span>}
          <span className="v2i-summary-note">{results ? "in these results" : pg && pg.totalPages > 1 ? `on this page (${pg.page}/${pg.totalPages})` : "shown"}</span>
        </div>
      )}

      {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={17} /> {err}<button type="button" className="v2-btn v2-btn--sm" onClick={() => load(page)}>Retry</button></div>}

      {/* ── List ────────────────────────────────────────────────────────── */}
      <section className={`v2-card v2i-card${loading && rows.length ? " is-refreshing" : ""}`}>
        {loading && !rows.length ? (
          <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Skel key={i} h={58} r={16} />)}</div>
        ) : rows.length === 0 ? (
          <div className="v2c-empty">
            <Duo name="invoice" size={48} />
            <strong>{results ? "No matches" : tab === "CANCELLED" ? "No cancelled invoices" : pay !== "ALL" ? `No ${pay.toLowerCase()} invoices` : "No invoices yet"}</strong>
            <span>{results ? "Try an invoice number, a client name, or a date like 2026-10." : tab === "ACTIVE" && pay === "ALL" ? "Create your first invoice to see it here." : "Nothing in this view."}</span>
            {!results && tab === "ACTIVE" && pay === "ALL" && canWrite && (
              <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => navigate("/dashboard/createinvoice")}>New invoice</button>
            )}
          </div>
        ) : view === "list" ? (
          <>
            {!isPhone && (
              <div className="v2i-head">
                <span className="v2i-cell-check">
                  {canWrite && selectableIds.length > 0 && <Check checked={allSel} indeterminate={!allSel && someSel} onChange={() => toggleAll(selectableIds)} label="Select all unpaid on this page" />}
                </span>
                <span className="v2i-h-main">Invoice</span>
                <span className="v2i-client">Client</span>
                <span className="v2i-date">Date</span>
                <span className="v2i-amount">Amount</span>
                <span className="v2i-status">Status</span>
                <span className="v2i-actions" />
              </div>
            )}
            <div className="v2i-rows" key={`${tab}${pay}${page}${results ? "s" : ""}`}>
              {rows.map((inv) => renderRow(inv, true))}
            </div>
          </>
        ) : (
          <div className="v2i-groups">
            {groups.map(([client, invs]) => {
              const isOpen = !!open[client];
              const due = invs.filter((i) => i.status === "ACTIVE").reduce((s, i) => s + Math.max(dueOf(i), 0), 0);
              const ids = invs.filter(selectable).map((i) => i._id);
              return (
                <div key={client} className={`v2i-group${isOpen ? " is-open" : ""}`}>
                  <button type="button" className="v2i-group-head" aria-expanded={isOpen} onClick={() => setOpen((o) => ({ ...o, [client]: !o[client] }))}>
                    <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13.5 }}>
                      {client.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                    </span>
                    <span className="v2i-main">
                      <span className="v2i-no">{client}</span>
                      <span className="v2i-sub">{invs.length} invoice{invs.length === 1 ? "" : "s"}{due > 0 ? <> · <b className="v2i-due">{inr(due)} due</b></> : " · all paid"}</span>
                    </span>
                    <span className="v2i-total">{inr(invs.reduce((s, i) => s + (i.grandTotal || 0), 0))}</span>
                    <Glyph name="chevronDown" size={18} className="v2i-chev" />
                  </button>
                  <div className="v2i-collapse"><div>
                    {canWrite && ids.length > 0 && (
                      <div className="v2i-group-tools">
                        <Check checked={ids.every((id) => selected.has(id))} indeterminate={ids.some((id) => selected.has(id)) && !ids.every((id) => selected.has(id))}
                          onChange={() => toggleAll(ids)} label={`Select unpaid invoices for ${client}`} />
                        <span>Select {ids.length} unpaid</span>
                      </div>
                    )}
                    {invs.map((inv) => renderRow(inv, false))}
                  </div></div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {!results && pg && pg.totalPages > 1 && (
        <nav className="v2i-pager" aria-label="Pages">
          <button type="button" className="v2-btn v2-btn--sm" disabled={!pg.hasPrev || loading} onClick={() => load(page - 1)}>
            <Glyph name="chevronLeft" size={16} stroke={2} /> Newer
          </button>
          <span>Page <b>{pg.page}</b> of {pg.totalPages}</span>
          <button type="button" className="v2-btn v2-btn--sm" disabled={!pg.hasNext || loading} onClick={() => load(page + 1)}>
            Older <Glyph name="chevronRight" size={16} stroke={2} />
          </button>
        </nav>
      )}

      {/* ── Floating bulk bar ───────────────────────────────────────────── */}
      <div className={`v2i-bulk${selected.size ? " is-on" : ""}`} role="region" aria-label="Bulk actions" aria-hidden={!selected.size}>
        <span className="v2i-bulk-count"><b>{selected.size}</b> selected</span>
        <button type="button" className="v2-btn v2-btn--sm" disabled={busy === "bulkcsv"} onClick={bulkCsv}>
          <Glyph name="download" size={15} /> {busy === "bulkcsv" ? "Preparing…" : "CSV"}
        </button>
        <button type="button" className="v2-btn v2-btn--sm v2-btn--primary" onClick={() => setConfirm({ kind: "markpaid" })}>
          <Glyph name="check" size={15} stroke={2.2} /> Mark paid
        </button>
        <button type="button" className="v2-icon-btn" aria-label="Clear selection" onClick={() => setSelected(new Set())}>
          <Glyph name="close" size={16} stroke={2} />
        </button>
      </div>

      {/* ── Sheets ──────────────────────────────────────────────────────── */}
      {sheets}

      <ExportSheet open={exportOpen} onClose={() => setExportOpen(false)} onError={(m) => say(m, "danger")} />
    </div>
  );
}

// ── Confirmations (destructive / external submissions) ──────────────────────
function ConfirmSheet({ state, busy, onClose, onConfirm, count }) {
  const keep = useRef(state);
  if (state) keep.current = state;
  const c = keep.current;
  if (!c) return null;
  const copy = {
    cancel: {
      title: `Cancel ${c.inv?.invoiceNumber}?`,
      body: "Stock on this invoice is restored and its ledger entries — including any payments — are removed. The invoice moves to Cancelled. This can't be undone.",
      cta: "Cancel invoice", danger: true,
    },
    irn: {
      title: `Generate IRN for ${c.inv?.invoiceNumber}?`,
      body: "This submits the invoice to the Invoice Registration Portal (IRP) through your GSP. Once generated, an IRN can only be cancelled within 24 hours.",
      cta: "Submit to IRP",
    },
    markpaid: {
      title: `Mark ${count} invoice${count === 1 ? "" : "s"} as paid?`,
      body: "Records a Cash payment for the full outstanding balance on each selected invoice and credits the client ledger.",
      cta: "Mark paid",
    },
  }[c.kind];
  return (
    <Sheet open={!!state} onClose={onClose} title={copy.title} width={460}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Not now</button>
        <button type="button" className={`v2-btn ${copy.danger ? "v2c-btn-danger" : "v2-btn--primary"}`} disabled={busy} onClick={onConfirm}>
          {busy ? "Working…" : copy.cta}
        </button>
      </>}>
      <p className="v2c-remove-text">{copy.body}</p>
      {c.error && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={17} /> {c.error}</div>}
    </Sheet>
  );
}

// ── Export CSV for a date range (GET /api/exports/invoices) ─────────────────
function ExportSheet({ open, onClose, onError }) {
  const today = new Date().toISOString().split("T")[0];
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const presets = [
    { label: "This month", f: monthStart, t: today },
    { label: "Last month", ...(() => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - 1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0); const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; return { f: iso(s), t: iso(e) }; })() },
    { label: "This FY", ...(() => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return { f: `${y}-04-01`, t: today }; })() },
  ];
  const go = async () => {
    if (!from || !to || from > to) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/exports/invoices?from=${from}&to=${to}`, { headers: auth() });
      if (!r.ok) throw new Error();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await r.blob());
      a.download = `Invoices_${from}_to_${to}.csv`;
      a.click();
      onClose();
    } catch { onError("Couldn't export invoices"); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Export invoices" subtitle="Raw invoice data as CSV" width={480}
      footer={<>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy || !from || !to || from > to} onClick={go}>
          {busy ? "Preparing…" : "Download CSV"}
        </button>
      </>}>
      <div className="v2-form">
        <div className="v2i-presets">
          {presets.map((p) => (
            <button key={p.label} type="button" className={`v2-chip-btn v2i-preset${from === p.f && to === p.t ? " is-on" : ""}`}
              onClick={() => { setFrom(p.f); setTo(p.t); }}>{p.label}</button>
          ))}
        </div>
        <div className="v2-form-row">
          <label className="v2-field"><span className="v2-field-label">From</span>
            <input className="v2-input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="v2-field"><span className="v2-field-label">To</span>
            <input className="v2-input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
        {from > to && <span className="v2-field-err">“From” must be on or before “To”.</span>}
      </div>
    </Sheet>
  );
}
